/**
 * Pending Universe Request banner.
 *
 * Surfaces when the user lands on Scrubber via a link with
 * ?universe_request_id=X — usually from an internal-fulfillment email that
 * Core sends when a recurring schedule fires against an internal department.
 *
 * The banner is purely informational in v1: it tells the user which request
 * is pending and what items it covers, so the Compliance team can correlate
 * uploads to the request. The upload itself flows through Scrubber's normal
 * UI (no special path) — keeps the audit-grade upload code untouched.
 */

import React, { useEffect, useState } from 'react';

function coreBase() {
  if (typeof window === 'undefined') return '';
  const isAzure = window.location.hostname.startsWith('az-');
  return `https://${isAzure ? 'az-core' : 'core'}.precisioncompliancegroup.com`;
}

function readQueryParam(name) {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(name);
}

export default function PendingUniverseRequestBanner() {
  const [request, setRequest] = useState(null);
  const [hidden, setHidden] = useState(false);
  const id = readQueryParam('universe_request_id');

  useEffect(() => {
    if (!id) return;
    fetch(`${coreBase()}/api/universe-requests/${encodeURIComponent(id)}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setRequest(d?.request || null))
      .catch(() => setRequest(null));
  }, [id]);

  if (!id || hidden || !request) return null;
  if (!['sent', 'in_progress'].includes(request.status)) return null;

  const items = (request.items || []).filter(i => i.consumer_app === 'scrubber');
  if (items.length === 0) return null;

  return (
    <div style={{
      background: 'linear-gradient(90deg, rgba(220,38,38,0.08), rgba(220,38,38,0.02))',
      borderLeft: '3px solid #b91c1c',
      padding: '12px 16px',
      margin: '12px 16px',
      fontSize: 13,
      color: '#1e293b',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#b91c1c', marginBottom: 4 }}>
          Pending Universe Request · cycle {request.sequence_number || '—'}
        </div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{request.title}</div>
        <div style={{ fontSize: 12, color: '#475569' }}>
          {items.length} item{items.length === 1 ? '' : 's'} expected:&nbsp;
          {items.map(i => i.catalog_label).join(' · ')}
          {request.reporting_period_start && request.reporting_period_end && (
            <> · period {request.reporting_period_start} → {request.reporting_period_end}</>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#475569', marginTop: 6 }}>
          Upload your file{items.length > 1 ? 's' : ''} using the form below. The Compliance team will see your submission against this request automatically.
        </div>
      </div>
      <button
        onClick={() => setHidden(true)}
        style={{
          background: 'transparent', border: 'none', color: '#64748b',
          fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1,
        }}
        title="Dismiss banner"
      >×</button>
    </div>
  );
}
